import { Component, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, finalize, firstValueFrom, take } from 'rxjs';
import {
  AlTableApi,
  AlTableColumn,
  AlTableGlobalAction,
  AlTableRowAction,
} from '../../../components/features/table/table.component';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { LearningBlockCaseDialogComponent } from '../../../components/dialogs/learning-block-case-dialog/learning-block-case-dialog.component';
import {
  LearningBlockCase,
  LearningBlockCaseDistillResult,
  LearningBlockCaseRun,
  LearningBlockCaseStatus,
  LearningBlockCaseThresholds,
  LearningBlockCasesOverview,
  LearningBlockCasesService,
  LearningBlockUsage,
  learningBlockCaseErrorText,
  learningBlockComponentLabel,
} from '../../../services/learning-block-cases.service';

/** Status filter above the cases table; `open` = suggestions and active cases. */
export type LearningBlockCaseFilter = 'open' | 'suggested' | 'active' | 'retired' | 'all';

type CaseGlobalAction = AlTableGlobalAction & { key: 'new' | 'distill' | 'distillForce' };

/** Suggestions first — they wait for a decision — then active, then retired. */
const STATUS_ORDER: Record<LearningBlockCaseStatus, number> = { suggested: 0, active: 1, retired: 2 };

/**
 * Lernfälle (super_admin, platform-wide): which interactive learning aid helps
 * in which situation. The nightly condensation of the usage telemetry writes
 * suggestions; only a case approved here (status `active`) reaches the agents
 * — al-tutor copies it into the LangGraph store. The second card shows how
 * each component was used in the condensation window.
 * PRD: features/prd/chat-learning-blocks-agent.md (Phase 3).
 *
 * Every array bound to `app-table` is assigned once per load or change, never
 * built in a getter (CLAUDE.md rule 18).
 */
@Component({
  selector: 'app-admin-learning-block-cases',
  templateUrl: './learning-block-cases.component.html',
  styleUrl: './learning-block-cases.component.scss',
  providers: [DatePipe],
})
export class LearningBlockCasesComponent implements OnInit {
  @ViewChild('casesTable') casesTable?: AlTableApi;

  /** First load: page loader instead of the cards (rule 19, stage B). */
  loading = true;
  /** The first load failed — nothing to show but the error and a retry. */
  loadFailed = false;
  /** A condensation run is in progress (rule 19, stage C). One at a time. */
  distilling: 'normal' | 'force' | null = null;
  /** A status change or delete is in flight; the table shows its overlay. */
  private rowBusy = false;

  cases: LearningBlockCase[] = [];
  filteredCases: LearningBlockCase[] = [];
  usage: LearningBlockUsage[] = [];
  components: string[] = [];
  thresholds: LearningBlockCaseThresholds = { min_impressions: 0, min_clients: 0, days: 30 };
  // Hint line under the usage table, formatted once per load. Plain text
  // without template whitespace: `.page-hint` keeps line breaks (pre-line).
  thresholdsText = '';
  lastRunText = '';
  lastRunError: string | null = null;

  statusFilter: LearningBlockCaseFilter = 'open';
  readonly statusFilters: LearningBlockCaseFilter[] = ['open', 'suggested', 'active', 'retired', 'all'];

  // Assigned once — app-table rebuilds on every new array (CLAUDE.md rule 18).
  caseColumns: AlTableColumn[] = [];
  caseRowActions: AlTableRowAction[] = [];
  caseGlobalActions: CaseGlobalAction[] = [];
  usageColumns: AlTableColumn[] = [];

  constructor(
    private casesService: LearningBlockCasesService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    // `instant()` returns raw keys until the language file is in (hard reload
    // straight onto this page); `get()` waits for it.
    const init = () => {
      this.buildTables();
      this.load(true);
    };
    this.translate.get('learningBlockCases.title').pipe(take(1)).subscribe({ next: init, error: init });
  }

  private buildTables(): void {
    const t = (key: string, params?: object) => this.t(key, params);
    const label = (id: string) => learningBlockComponentLabel(this.translate, id);

    this.caseColumns = [
      { key: 'component', title: t('col.component'), width: '110px', map: (v: string) => label(v) },
      // Free text up to 300 characters: the main column keeps a readable
      // minimum width and, like the action column, breaks long words.
      { key: 'situation', kind: 'name', title: t('col.situation') },
      { key: 'action', kind: 'text', title: t('col.action') },
      { key: 'polarity', title: t('col.polarity'), width: '90px', map: (v: string) => t('polarity.' + v) },
      {
        key: 'evidence', kind: 'number', title: t('col.evidence'), width: '150px',
        map: (_: unknown, row: LearningBlockCase) => this.evidenceText(row),
      },
      { key: 'status', title: t('col.status'), width: '110px', map: (v: string) => t('status.' + v) },
      { key: 'source', title: t('col.source'), width: '120px', map: (v: string) => t('source.' + v) },
    ];
    this.caseRowActions = [
      { key: 'approve', title: t('action.approve'), icon: 'checkmark-circle-2-outline',
        disabled: (r: LearningBlockCase) => r.status !== 'suggested' },
      { key: 'retire', title: t('action.retire'), icon: 'archive-outline',
        disabled: (r: LearningBlockCase) => r.status !== 'active' },
      { key: 'resuggest', title: t('action.resuggest'), icon: 'undo-outline',
        disabled: (r: LearningBlockCase) => r.status !== 'retired' },
      { key: 'edit', title: t('action.edit'), icon: 'edit-outline' },
      { key: 'delete', title: t('action.delete'), icon: 'trash-2-outline' },
    ];
    this.caseGlobalActions = [
      { key: 'new', title: t('action.new'), icon: 'plus-outline' },
      { key: 'distill', title: t('action.distill'), icon: 'refresh-outline' },
      { key: 'distillForce', title: t('action.distillForce'), icon: 'play-circle-outline' },
    ];

    const count = (v: number) => (typeof v === 'number' ? v.toLocaleString('de-DE') : '–');
    this.usageColumns = [
      { key: 'component', title: t('usage.col.component'), map: (v: string) => label(v) },
      { key: 'impressions', kind: 'number', title: t('usage.col.impressions'), map: count },
      { key: 'failed', kind: 'number', title: t('usage.col.failed'), map: count },
      { key: 'engaged', kind: 'number', title: t('usage.col.engaged'), map: count },
      { key: 'completed', kind: 'number', title: t('usage.col.completed'), map: count },
      { key: 'clients', kind: 'number', title: t('usage.col.clients'), map: count },
      {
        key: 'thumbs_up', kind: 'number', title: t('usage.col.thumbs'),
        map: (_: unknown, r: LearningBlockUsage) => `${count(r.thumbs_up)} / ${count(r.thumbs_down)}`,
      },
      { key: 'shadow_agree', kind: 'number', title: t('usage.col.shadow'), map: count },
    ];
  }

  // ---------------------------------------------------------------- loading

  load(initial = false): void {
    if (initial) {
      this.loading = true;
      this.loadFailed = false;
    } else if (this.casesTable) {
      // Reload keeps the rows on screen; app-table lays its overlay on top.
      this.casesTable.isLoading = true;
    }
    this.casesService
      .getOverview()
      .pipe(
        finalize(() => {
          this.loading = false;
          if (this.casesTable) this.casesTable.isLoading = false;
        })
      )
      .subscribe({
        next: (overview) => {
          this.loadFailed = false;
          this.applyOverview(overview);
        },
        error: (err) => {
          if (initial) {
            this.loadFailed = true;
          } else {
            this.toastError(err);
          }
        },
      });
  }

  private applyOverview(o: LearningBlockCasesOverview): void {
    this.components = o.components ?? [];
    this.thresholds = o.thresholds ?? this.thresholds;
    this.thresholdsText = this.t('usage.thresholds', {
      min_impressions: this.thresholds.min_impressions,
      min_clients: this.thresholds.min_clients,
    });
    const run = o.last_run ?? null;
    this.lastRunText = this.formatLastRun(run);
    this.lastRunError = run?.error ? this.t('usage.lastRunError', { error: run.error }) : null;
    this.usage = [...(o.usage ?? [])].sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0));
    // Sorted on load only: a row that changes status stays where it is.
    this.cases = [...(o.cases ?? [])].sort(
      (a, b) =>
        (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
        String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? ''))
    );
    this.applyFilter();
  }

  onStatusFilterChange(filter: LearningBlockCaseFilter): void {
    this.statusFilter = filter;
    this.applyFilter();
  }

  private applyFilter(): void {
    const f = this.statusFilter;
    this.filteredCases = this.cases.filter((c) =>
      f === 'all' ? true : f === 'open' ? c.status !== 'retired' : c.status === f
    );
  }

  // ------------------------------------------------------------ table events

  onCaseEvent(e: { event: string; data: any }): void {
    if (e.event === 'globalAction') {
      this.onGlobalAction((e.data?.action as CaseGlobalAction)?.key);
      return;
    }
    if (e.event !== 'rowAction') return;
    const row = e.data.row as LearningBlockCase;
    switch (e.data.action?.key) {
      case 'approve':
        this.changeStatus(row, 'active', 'toast.approved');
        break;
      case 'retire':
        this.changeStatus(row, 'retired', 'toast.retired');
        break;
      case 'resuggest':
        this.changeStatus(row, 'suggested', 'toast.resuggested');
        break;
      case 'edit':
        this.openCaseDialog(row);
        break;
      case 'delete':
        this.deleteCase(row);
        break;
    }
  }

  private onGlobalAction(key: CaseGlobalAction['key'] | undefined): void {
    if (this.distilling || this.rowBusy) return;
    switch (key) {
      case 'new':
        this.openCaseDialog();
        break;
      case 'distill':
        this.distill(false);
        break;
      case 'distillForce':
        this.distill(true);
        break;
    }
  }

  // ------------------------------------------------------------------ cases

  openCaseDialog(learningCase?: LearningBlockCase): void {
    this.dialogService
      .open(LearningBlockCaseDialogComponent, {
        context: { learningCase, components: this.components },
      })
      .onClose.subscribe((saved?: LearningBlockCase) => {
        if (!saved) return;
        this.upsertCase(saved);
        this.toastr.success(this.t(learningCase ? 'toast.saved' : 'toast.created'), '');
      });
  }

  private changeStatus(row: LearningBlockCase, status: LearningBlockCaseStatus, toastKey: string): void {
    if (row.status === status) return;
    this.runRowAction(this.casesService.setStatus(row.id, status), (saved) => {
      this.upsertCase(saved);
      this.toastr.success(this.t(toastKey), '');
    });
  }

  private async deleteCase(row: LearningBlockCase): Promise<void> {
    const ok = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: 'learningBlockCases.deleteConfirm.title',
          message: 'learningBlockCases.deleteConfirm.message',
          confirmLabel: 'buttons.delete',
        },
      }).onClose
    );
    if (!ok) return;
    this.runRowAction(this.casesService.deleteCase(row.id), () => {
      this.cases = this.cases.filter((c) => c.id !== row.id);
      this.applyFilter();
      this.toastr.success(this.t('toast.deleted'), '');
    });
  }

  /** One row action at a time; the table overlay covers the wait. */
  private runRowAction<T>(request$: Observable<T>, done: (result: T) => void): void {
    if (this.rowBusy || this.distilling) return;
    this.rowBusy = true;
    if (this.casesTable) this.casesTable.isLoading = true;
    request$
      .pipe(
        finalize(() => {
          this.rowBusy = false;
          if (this.casesTable) this.casesTable.isLoading = false;
        })
      )
      .subscribe({ next: done, error: (err) => this.toastError(err) });
  }

  /** Replace the row in place (a new case goes on top), then re-filter. */
  private upsertCase(saved: LearningBlockCase): void {
    const exists = this.cases.some((c) => c.id === saved.id);
    this.cases = exists
      ? this.cases.map((c) => (c.id === saved.id ? saved : c))
      : [saved, ...this.cases];
    this.applyFilter();
  }

  // -------------------------------------------------------------- distill

  private async distill(force: boolean): Promise<void> {
    if (force) {
      const ok = await firstValueFrom(
        this.dialogService.open(ConfirmDialogComponent, {
          context: {
            title: 'learningBlockCases.forceConfirm.title',
            message: this.t('forceConfirm.message', {
              min_impressions: this.thresholds.min_impressions,
              min_clients: this.thresholds.min_clients,
            }),
            confirmLabel: 'learningBlockCases.forceConfirm.confirm',
          },
        }).onClose
      );
      if (!ok) return;
    }
    if (this.distilling || this.rowBusy) return;
    this.distilling = force ? 'force' : 'normal';
    this.casesService
      .distill(force)
      .pipe(finalize(() => (this.distilling = null)))
      .subscribe({
        next: (result) => {
          this.showDistillResult(result);
          this.load();
        },
        error: (err) => this.toastError(err),
      });
  }

  private showDistillResult(r: LearningBlockCaseDistillResult): void {
    const label = (id: string) => learningBlockComponentLabel(this.translate, id);
    const created = r.created ?? 0;
    const parts = [
      created === 0
        ? this.t('distill.createdNone')
        : created === 1
          ? this.t('distill.createdOne')
          : this.t('distill.createdMany', { count: created }),
    ];
    if (r.eligible?.length) {
      parts.push(this.t('distill.eligible', { list: r.eligible.map(label).join(', ') }));
    }
    const skipped = Object.entries(r.skipped ?? {});
    if (skipped.length) {
      const list = skipped.map(([component, reason]) => `${label(component)} (${reason})`).join(', ');
      parts.push(this.t('distill.skipped', { list }));
    }
    const message = parts.join(' ');
    // Long enough to read the skip reasons (default is 3 s).
    const config = { duration: skipped.length ? 12000 : 6000 };
    if (r.error) {
      this.toastr.danger(`${this.t('distill.error', { error: r.error })} ${message}`, '', { duration: 12000 });
    } else if (created > 0) {
      this.toastr.success(message, '', config);
    } else {
      this.toastr.info(message, '', config);
    }
  }

  // ----------------------------------------------------------- formatting

  /** "40 · 62 % · 4 Mandanten" — impressions, share with interaction, clients. */
  private evidenceText(c: LearningBlockCase): string {
    const e = c.evidence || {};
    const parts: string[] = [];
    if (typeof e.n === 'number') {
      parts.push(e.n.toLocaleString('de-DE'));
    }
    if (typeof e.engagement === 'number') {
      parts.push(this.t('evidence.engagement', { pct: Math.round(e.engagement * 100) }));
    }
    if (typeof e.clients === 'number') {
      parts.push(e.clients === 1 ? this.t('evidence.clientsOne') : this.t('evidence.clientsMany', { count: e.clients }));
    }
    if (e.forced) {
      parts.push(this.t('evidence.forced'));
    }
    if (parts.length) return parts.join(' · ');
    return c.source === 'manual' ? this.t('evidence.manual') : '–';
  }

  private formatLastRun(run: LearningBlockCaseRun | null): string {
    if (!run) return this.t('usage.noRun');
    const time = (v: string | null) => (v ? this.datePipe.transform(v, 'dd.MM.yyyy HH:mm') || v : '');
    return run.finished_at
      ? this.t('usage.lastRun', { key: run.run_key, time: time(run.finished_at) })
      : this.t('usage.lastRunOpen', { key: run.run_key, time: time(run.started_at) });
  }

  private t(key: string, params?: object): string {
    return this.translate.instant('learningBlockCases.' + key, params);
  }

  private toastError(err?: any): void {
    this.toastr.danger(learningBlockCaseErrorText(err) || this.t('error'), '');
  }
}
